import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch, getApiHost } from '../utils/api';

const API_BASE_URL = getApiHost();

interface CreditTransaction {
  id: string;
  type: 'addition' | 'usage';
  amount: number;
  balanceAfter: number;
  category: string;
  description: string;
  relatedEntity: string | null;
  createdAt: string;
}

interface PaginationInfo {
  page: number;
  limit: number;
  totalCount: number;
  totalPages: number;
  hasMore: boolean;
}

interface CreditHistoryProps {
  className?: string;
}

const categoryLabels: Record<string, string> = {
  signup_bonus: 'Signup Bonus',
  purchase: 'Purchase',
  holder_research: 'Holder Research',
  social_research: 'Social Research',
  api_call: 'API Call',
  ai_task: 'AI Task',
};

const categoryColors: Record<string, string> = {
  signup_bonus: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  purchase: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  holder_research: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  social_research: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
  api_call: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  ai_task: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
};

const CreditHistory: React.FC<CreditHistoryProps> = ({ className = '' }) => {
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const getToken = (): string | null => {
    return localStorage.getItem('deepnets_token');
  };

  const fetchHistory = useCallback(async (page: number) => {
    const token = getToken();
    if (!token) {
      setError('Not authenticated');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const response = await apiFetch(
        `${API_BASE_URL}/api/credits/history?page=${page}&limit=10`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch credit history');
      }

      const data = await response.json();
      setTransactions(data.transactions);
      setPagination(data.pagination);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory(currentPage);
  }, [currentPage, fetchHistory]);

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading && transactions.length === 0) {
    return (
      <div className={`${className}`}>
        <div className="animate-pulse space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-700/50 rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${className}`}>
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-center">
          <p className="text-red-400">{error}</p>
          <button
            onClick={() => fetchHistory(currentPage)}
            className="mt-2 text-sm text-red-300 hover:text-red-200 underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (transactions.length === 0) {
    return (
      <div className={`${className}`}>
        <div className="bg-gray-700/30 rounded-lg p-8 text-center">
          <svg className="w-12 h-12 text-gray-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <p className="text-gray-400">No credit transactions yet</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${className}`}>
      {/* Table header */}
      <div className="hidden sm:grid grid-cols-12 gap-4 px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">
        <div className="col-span-3">Date</div>
        <div className="col-span-2">Type</div>
        <div className="col-span-2 text-right">Amount</div>
        <div className="col-span-2">Category</div>
        <div className="col-span-3">Description</div>
      </div>

      {/* Transactions */}
      <div className="space-y-2">
        {transactions.map((tx) => (
          <div
            key={tx.id}
            className="bg-gray-700/30 hover:bg-gray-700/50 rounded-lg p-4 transition-colors"
          >
            {/* Mobile layout */}
            <div className="sm:hidden space-y-2">
              <div className="flex items-center justify-between">
                <span className={`text-lg font-semibold ${
                  tx.type === 'addition' ? 'text-crypto-accent' : 'text-crypto-red'
                }`}>
                  {tx.type === 'addition' ? '+' : '-'}{tx.amount}
                </span>
                <span className={`px-2 py-0.5 text-xs rounded-full border ${
                  categoryColors[tx.category] || 'bg-gray-500/20 text-gray-400 border-gray-500/30'
                }`}>
                  {categoryLabels[tx.category] || tx.category}
                </span>
              </div>
              <p className="text-gray-400 text-sm">{tx.description}</p>
              <p className="text-gray-500 text-xs">{formatDate(tx.createdAt)}</p>
            </div>

            {/* Desktop layout */}
            <div className="hidden sm:grid grid-cols-12 gap-4 items-center">
              <div className="col-span-3 text-gray-400 text-sm">
                {formatDate(tx.createdAt)}
              </div>
              <div className="col-span-2">
                <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded ${
                  tx.type === 'addition'
                    ? 'bg-crypto-accent/20 text-crypto-accent'
                    : 'bg-crypto-red/20 text-crypto-red'
                }`}>
                  {tx.type === 'addition' ? 'Credit' : 'Usage'}
                </span>
              </div>
              <div className={`col-span-2 text-right font-semibold ${
                tx.type === 'addition' ? 'text-crypto-accent' : 'text-crypto-red'
              }`}>
                {tx.type === 'addition' ? '+' : '-'}{tx.amount}
              </div>
              <div className="col-span-2">
                <span className={`px-2 py-0.5 text-xs rounded-full border ${
                  categoryColors[tx.category] || 'bg-gray-500/20 text-gray-400 border-gray-500/30'
                }`}>
                  {categoryLabels[tx.category] || tx.category}
                </span>
              </div>
              <div className="col-span-3 text-gray-400 text-sm truncate" title={tx.description}>
                {tx.description}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700">
          <p className="text-sm text-gray-500">
            Showing {((currentPage - 1) * pagination.limit) + 1} to{' '}
            {Math.min(currentPage * pagination.limit, pagination.totalCount)} of{' '}
            {pagination.totalCount} transactions
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(p => p - 1)}
              disabled={currentPage <= 1}
              className="px-3 py-1 text-sm bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-600 text-white rounded transition-colors"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(p => p + 1)}
              disabled={!pagination.hasMore}
              className="px-3 py-1 text-sm bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-600 text-white rounded transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditHistory;
